import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Search,
  X,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  MoveRight,
  ClipboardList,
  Copy,
  AlertTriangle,
  Info,
  ExternalLink,
  Filter,
  Settings2,
  Loader2,
  Package,
  PackageOpen,
  Download,
  Plus,
  BarChart3,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { StockItemDrawerDetails } from "@/components/stock-item-drawer-details";
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AdvancedFiltersModal } from "@/components/advanced-filters-modal";
import { FilterRow, FilterLogic, ActiveFilterChip, FilterField } from "@/types/filters";
import { applyFilters, generateFilterChips } from "@/lib/filter-utils";
import { ConfigureColumnsModal, ColumnDefinition, ColumnConfig } from "@/components/configure-columns-modal";
import {
  exportToCSV,
  exportToExcel,
  getCurrentDateForFilename,
  prepareDataForExport,
  getVisibleColumns,
  getAllColumns,
} from "@/lib/export-utils";
import { useNavigation } from "@/components/layout/app-layout";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { StockItem } from "@/types/stock-item";
import { getTransactionCount } from "@/data/mockTransactions";
import { mockStockItems as mockStockItemsFromData } from "@/data/mockStockItems";
import { useStockCosting } from "@/hooks/use-stock-costing";
import { StockItemDeleteDialog } from "@/components/stock-item-delete-dialog";
import { StockMovementStartDialog } from "@/components/stock-movement-start-dialog";
import { PreparedStockMovementItem } from "@/lib/stock-movement-start-validation";
import { MovementTypeSelectionDialog } from "@/components/movement-type-selection-dialog";
import { StockItemDuplicateDialog } from "@/components/stock-item-duplicate-dialog";
import {
  validateDuplication,
  prepareDuplicateData,
  DuplicateItem,
  DuplicateValidationResult,
} from "@/lib/stock-item-duplicate-logic";
import { StockTakeStartDialog } from "@/components/stock-take-start-dialog";
import { validateAndPrepareStockTake, StockTakeStartValidationResult } from "@/lib/stock-take-start-validation";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { HelpButton } from "@/components/help-button";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { useCustomFields } from "@/hooks/use-custom-fields";
import { useStockItems } from "@/hooks/use-stock-items";
import { DataTableWrapper } from "@/components/ui/data-table-wrapper";
import { useScreenMode, SHOW_DEMO_MODE_BADGES } from "@/providers/ModeProvider";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";
import { useWarehouseStockValue } from "@/hooks/use-warehouse-stock-value";

// All available columns with descriptions
const ALL_COLUMNS: ColumnDefinition[] = [
  { id: "code", name: "Code", description: "Stock item identifier" },
  { id: "description", name: "Description", description: "Stock item description" },
  { id: "category", name: "Category", description: "Product category" },
  { id: "type", name: "Type", description: "Item behavior (Purchased, Manufactured, Assembly, etc.)" },
  { id: "unitOfMeasure", name: "Unit of Measure", description: "How the item is counted (Each, Kg, etc.)" },
  { id: "location", name: "Location", description: "Single or multiple locations" },
  { id: "physicalQuantity", name: "Physical Quantity", description: "Current physical stock" },
  { id: "allocatedQuantity", name: "Allocated Quantity", description: "Stock allocated to orders" },
  { id: "freeQuantity", name: "Free Quantity", description: "Available stock (Physical - Allocated)" },
  { id: "quantityDueIn", name: "Quantity Due In", description: "Stock on order from suppliers" },
  { id: "unitPrice", name: "Unit Price", description: "Selling price per unit" },
  { id: "value", name: "Value", description: "Total value (Quantity × Price)" },
  { id: "totalCostOfSales", name: "Total Cost of Sales", description: "Lifetime cost of goods sold" },
  { id: "totalValueOfSales", name: "Total Value of Sales", description: "Lifetime revenue generated" },
  { id: "status", name: "Status", description: "Active or Inactive" },
  { id: "weight", name: "Weight", description: "Item weight" },
  { id: "barcode", name: "Barcode", description: "Product barcode" },
  { id: "binNumber", name: "Bin Number", description: "Storage bin location" },
  { id: "minQuantity", name: "Min Quantity", description: "Minimum stock level" },
  { id: "maxQuantity", name: "Max Quantity", description: "Maximum stock level" },
  { id: "reorderPoint", name: "Reorder Point", description: "Reorder trigger level" },
  { id: "reorderQuantity", name: "Reorder Quantity", description: "Default reorder amount" },
  { id: "lastCostPrice", name: "Last Cost Price", description: "Most recent purchase cost" },
  { id: "averageCost", name: "Average Cost", description: "Average cost across purchases" },
  { id: "grossMarginPercent", name: "Gross Margin %", description: "Profit margin percentage" },
  { id: "purchaseAnalysisCode", name: "Purchase Analysis Code", description: "Purchase reporting code" },
  { id: "salesAnalysisCode", name: "Sales Analysis Code", description: "Sales reporting code" },
  { id: "movementInAnalysis", name: "Movement In Analysis", description: "Inbound movement code" },
  { id: "movementOutAnalysis", name: "Movement Out Analysis", description: "Outbound movement code" },
  { id: "abcClassification", name: "ABC Classification", description: "Value classification (A/B/C)" },
  { id: "stockTurnRate", name: "Stock Turn Rate", description: "Annual turnover rate" },
  { id: "supplier", name: "Supplier", description: "Primary supplier name" },
  { id: "leadTimeDays", name: "Lead Time Days", description: "Supplier delivery time" },
  { id: "lastPurchaseDate", name: "Last Purchase Date", description: "Date of last purchase" },
  { id: "lastSaleDate", name: "Last Sale Date", description: "Date of last sale" },
  { id: "lastMovementDate", name: "Last Movement Date", description: "Date of last stock movement" },
];

// Default visible columns (11 columns)
const DEFAULT_VISIBLE_COLUMNS = [
  "code",
  "description",
  "category",
  "type",
  "unitOfMeasure",
  "location",
  "physicalQuantity",
  "unitPrice",
  "value",
  "status",
  "lastMovementDate",
];

type SortColumn = "code" | "description" | "category" | "type" | "physicalQuantity" | "unitPrice" | "value" | "lastMovementDate";
type SortDirection = "asc" | "desc";

// Generate 50+ mock stock items with barcodes
const generateMockStockItems = (): StockItem[] => {
  // Temporary interface for initial data with location string
  interface TempItem {
    code: string;
    description: string;
    category: string;
    location: string;
    physicalQuantity: number;
    unitPrice: number;
    inactive: boolean;
    barcode: string;
  }

  const items: TempItem[] = [
    {
      code: "STK001",
      description: "Laptop Dell XPS 15",
      category: "Electronics",
      location: "Warehouse A",
      physicalQuantity: 45,
      unitPrice: 1299.99,
      inactive: false,
      barcode: "5901234123457",
    },
    {
      code: "STK002",
      description: "Office Chair Ergonomic",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 120,
      unitPrice: 249.5,
      inactive: false,
      barcode: "5901234123464",
    },
    {
      code: "STK003",
      description: "A4 Paper Ream (500 sheets)",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 450,
      unitPrice: 5.99,
      inactive: false,
      barcode: "5901234123471",
    },
    {
      code: "STK004",
      description: "Steel Rods 10mm x 6m",
      category: "Raw Materials",
      location: "Distribution Center",
      physicalQuantity: 200,
      unitPrice: 45.75,
      inactive: false,
      barcode: "5901234123488",
    },
    {
      code: "STK005",
      description: "Wireless Mouse Logitech",
      category: "Electronics",
      location: "Retail Store",
      physicalQuantity: 85,
      unitPrice: 29.99,
      inactive: false,
      barcode: "5901234123495",
    },
    {
      code: "STK006",
      description: "Standing Desk Adjustable",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 35,
      unitPrice: 599.0,
      inactive: false,
      barcode: "5901234123501",
    },
    {
      code: "STK007",
      description: "Ballpoint Pens Blue (Box of 50)",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 300,
      unitPrice: 12.5,
      inactive: false,
      barcode: "5901234123518",
    },
    {
      code: "STK008",
      description: "Aluminum Sheets 2mm",
      category: "Raw Materials",
      location: "Warehouse C",
      physicalQuantity: 150,
      unitPrice: 89.99,
      inactive: true,
      barcode: "5901234123525",
    },
    {
      code: "STK009",
      description: 'Monitor 27" 4K Dell',
      category: "Electronics",
      location: "Warehouse A",
      physicalQuantity: 60,
      unitPrice: 449.99,
      inactive: false,
      barcode: "5901234123532",
    },
    {
      code: "STK010",
      description: "Filing Cabinet 4-Drawer",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 40,
      unitPrice: 189.99,
      inactive: false,
      barcode: "5901234123549",
    },
    {
      code: "STK011",
      description: "Stapler Heavy Duty",
      category: "Office Supplies",
      location: "Retail Store",
      physicalQuantity: 95,
      unitPrice: 18.99,
      inactive: false,
      barcode: "5901234123556",
    },
    {
      code: "STK012",
      description: "Copper Wire 2.5mm",
      category: "Raw Materials",
      location: "Distribution Center",
      physicalQuantity: 500,
      unitPrice: 3.25,
      inactive: false,
      barcode: "5901234123563",
    },
    {
      code: "STK013",
      description: "Keyboard Mechanical RGB",
      category: "Electronics",
      location: "Warehouse A",
      physicalQuantity: 70,
      unitPrice: 129.99,
      inactive: false,
      barcode: "5901234123570",
    },
    {
      code: "STK014",
      description: "Conference Table 8-Seater",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 12,
      unitPrice: 899.0,
      inactive: false,
      barcode: "5901234123587",
    },
    {
      code: "STK015",
      description: "Sticky Notes 3x3 (Pack of 12)",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 280,
      unitPrice: 8.99,
      inactive: false,
      barcode: "5901234123594",
    },
    {
      code: "STK016",
      description: "PVC Pipes 50mm x 3m",
      category: "Raw Materials",
      location: "Warehouse C",
      physicalQuantity: 180,
      unitPrice: 15.5,
      inactive: false,
      barcode: "5901234123600",
    },
    {
      code: "STK017",
      description: "Webcam HD 1080p",
      category: "Electronics",
      location: "Retail Store",
      physicalQuantity: 55,
      unitPrice: 79.99,
      inactive: false,
      barcode: "5901234123617",
    },
    {
      code: "STK018",
      description: "Bookshelf 5-Tier",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 65,
      unitPrice: 149.99,
      inactive: false,
      barcode: "5901234123624",
    },
    {
      code: "STK019",
      description: "Printer Paper A3 (250 sheets)",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 220,
      unitPrice: 9.99,
      inactive: false,
      barcode: "5901234123631",
    },
    {
      code: "STK020",
      description: "Timber Planks Oak 2x4",
      category: "Raw Materials",
      location: "Distribution Center",
      physicalQuantity: 300,
      unitPrice: 12.75,
      inactive: false,
      barcode: "5901234123648",
    },
    {
      code: "STK021",
      description: "USB-C Hub 7-Port",
      category: "Electronics",
      location: "Warehouse A",
      physicalQuantity: 90,
      unitPrice: 45.99,
      inactive: false,
      barcode: "5901234123655",
    },
    {
      code: "STK022",
      description: "Desk Lamp LED Adjustable",
      category: "Furniture",
      location: "Retail Store",
      physicalQuantity: 110,
      unitPrice: 34.99,
      inactive: false,
      barcode: "5901234123662",
    },
    {
      code: "STK023",
      description: "Whiteboard Markers (Set of 8)",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 175,
      unitPrice: 14.99,
      inactive: false,
      barcode: "5901234123679",
    },
    {
      code: "STK024",
      description: "Stainless Steel Bolts M8",
      category: "Raw Materials",
      location: "Warehouse C",
      physicalQuantity: 1000,
      unitPrice: 0.45,
      inactive: false,
      barcode: "5901234123686",
    },
    {
      code: "STK025",
      description: "External SSD 1TB Samsung",
      category: "Electronics",
      location: "Warehouse A",
      physicalQuantity: 40,
      unitPrice: 149.99,
      inactive: false,
      barcode: "5901234123693",
    },
    {
      code: "STK026",
      description: "Office Partition Panel",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 50,
      unitPrice: 199.0,
      inactive: true,
      barcode: "5901234123709",
    },
    {
      code: "STK027",
      description: "Envelope C5 (Box of 500)",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 160,
      unitPrice: 19.99,
      inactive: false,
      barcode: "5901234123716",
    },
    {
      code: "STK028",
      description: "Plastic Granules HDPE",
      category: "Raw Materials",
      location: "Distribution Center",
      physicalQuantity: 400,
      unitPrice: 2.85,
      inactive: false,
      barcode: "5901234123723",
    },
    {
      code: "STK029",
      description: "Headset Wireless Noise-Cancelling",
      category: "Electronics",
      location: "Retail Store",
      physicalQuantity: 75,
      unitPrice: 199.99,
      inactive: false,
      barcode: "5901234123730",
    },
    {
      code: "STK030",
      description: "Storage Cabinet Metal",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 30,
      unitPrice: 279.99,
      inactive: false,
      barcode: "5901234123747",
    },
    {
      code: "STK031",
      description: "Correction Tape (Pack of 10)",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 140,
      unitPrice: 11.99,
      inactive: false,
      barcode: "5901234123754",
    },
    {
      code: "STK032",
      description: "Rubber Sheets 5mm",
      category: "Raw Materials",
      location: "Warehouse C",
      physicalQuantity: 90,
      unitPrice: 35.5,
      inactive: false,
      barcode: "5901234123761",
    },
    {
      code: "STK033",
      description: 'Tablet iPad 10.2"',
      category: "Electronics",
      location: "Warehouse A",
      physicalQuantity: 35,
      unitPrice: 449.0,
      inactive: false,
      barcode: "5901234123778",
    },
    {
      code: "STK034",
      description: "Visitor Chair Stackable",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 200,
      unitPrice: 79.99,
      inactive: false,
      barcode: "5901234123785",
    },
    {
      code: "STK035",
      description: "Binder Clips Assorted (Box of 100)",
      category: "Office Supplies",
      location: "Retail Store",
      physicalQuantity: 190,
      unitPrice: 7.99,
      inactive: false,
      barcode: "5901234123792",
    },
    {
      code: "STK036",
      description: "Ceramic Tiles 30x30cm",
      category: "Raw Materials",
      location: "Distribution Center",
      physicalQuantity: 500,
      unitPrice: 4.25,
      inactive: false,
      barcode: "5901234123808",
    },
    {
      code: "STK037",
      description: "Printer Laser Brother",
      category: "Electronics",
      location: "Warehouse A",
      physicalQuantity: 25,
      unitPrice: 299.99,
      inactive: false,
      barcode: "5901234123815",
    },
    {
      code: "STK038",
      description: "Executive Desk L-Shaped",
      category: "Furniture",
      location: "Warehouse B",
      physicalQuantity: 18,
      unitPrice: 749.0,
      inactive: false,
      barcode: "5901234123822",
    },
    {
      code: "STK039",
      description: "Calculator Scientific",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 130,
      unitPrice: 24.99,
      inactive: false,
      barcode: "5901234123839",
    },
    {
      code: "STK040",
      description: "Glass Sheets 4mm Tempered",
      category: "Raw Materials",
      location: "Warehouse C",
      physicalQuantity: 80,
      unitPrice: 125.0,
      inactive: true,
      barcode: "5901234123846",
    },
    {
      code: "STK041",
      description: "Power Drill Cordless",
      category: "Tools",
      location: "Warehouse A",
      physicalQuantity: 55,
      unitPrice: 159.99,
      inactive: false,
      barcode: "5901234123853",
    },
    {
      code: "STK042",
      description: "Toolbox Metal 5-Drawer",
      category: "Tools",
      location: "Warehouse B",
      physicalQuantity: 40,
      unitPrice: 189.99,
      inactive: false,
      barcode: "5901234123860",
    },
    {
      code: "STK043",
      description: "Screwdriver Set 20-Piece",
      category: "Tools",
      location: "Retail Store",
      physicalQuantity: 95,
      unitPrice: 39.99,
      inactive: false,
      barcode: "5901234123877",
    },
    {
      code: "STK044",
      description: "Cardboard Boxes Large (Pack of 25)",
      category: "Packaging",
      location: "Warehouse A",
      physicalQuantity: 350,
      unitPrice: 29.99,
      inactive: false,
      barcode: "5901234123884",
    },
    {
      code: "STK045",
      description: "Bubble Wrap Roll 50m",
      category: "Packaging",
      location: "Distribution Center",
      physicalQuantity: 180,
      unitPrice: 18.5,
      inactive: false,
      barcode: "5901234123891",
    },
    {
      code: "STK046",
      description: "Packing Tape Clear (6-Pack)",
      category: "Packaging",
      location: "Warehouse A",
      physicalQuantity: 240,
      unitPrice: 12.99,
      inactive: false,
      barcode: "5901234123907",
    },
    {
      code: "STK047",
      description: "Safety Goggles Anti-Fog",
      category: "Safety Equipment",
      location: "Warehouse C",
      physicalQuantity: 150,
      unitPrice: 14.99,
      inactive: false,
      barcode: "5901234123914",
    },
    {
      code: "STK048",
      description: "Hard Hat Yellow",
      category: "Safety Equipment",
      location: "Warehouse B",
      physicalQuantity: 200,
      unitPrice: 19.99,
      inactive: false,
      barcode: "5901234123921",
    },
    {
      code: "STK049",
      description: "Safety Gloves Cut-Resistant",
      category: "Safety Equipment",
      location: "Warehouse A",
      physicalQuantity: 300,
      unitPrice: 8.99,
      inactive: false,
      barcode: "5901234123938",
    },
    {
      code: "STK050",
      description: "Fire Extinguisher 5kg",
      category: "Safety Equipment",
      location: "Retail Store",
      physicalQuantity: 45,
      unitPrice: 89.99,
      inactive: false,
      barcode: "5901234123945",
    },
    {
      code: "STK051",
      description: "Label Maker Portable",
      category: "Office Supplies",
      location: "Warehouse A",
      physicalQuantity: 60,
      unitPrice: 49.99,
      inactive: false,
      barcode: "5901234123952",
    },
    {
      code: "STK052",
      description: "Shredder Cross-Cut",
      category: "Office Supplies",
      location: "Warehouse B",
      physicalQuantity: 28,
      unitPrice: 129.99,
      inactive: false,
      barcode: "5901234123969",
    },
    {
      code: "STK053",
      description: "Laminator A4",
      category: "Office Supplies",
      location: "Retail Store",
      physicalQuantity: 35,
      unitPrice: 69.99,
      inactive: false,
      barcode: "5901234123976",
    },
    {
      code: "STK054",
      description: "Paint Rollers (Set of 5)",
      category: "Tools",
      location: "Warehouse C",
      physicalQuantity: 120,
      unitPrice: 22.99,
      inactive: false,
      barcode: "5901234123983",
    },
    {
      code: "STK055",
      description: "Measuring Tape 10m",
      category: "Tools",
      location: "Warehouse A",
      physicalQuantity: 85,
      unitPrice: 12.99,
      inactive: false,
      barcode: "5901234123990",
    },
  ];

  // Calculate value and add random dates and quantity fields
  return items.map((item) => {
    const daysAgo = Math.floor(Math.random() * 90); // Random date within last 90 days
    const lastMovementDate = new Date();
    lastMovementDate.setDate(lastMovementDate.getDate() - daysAgo);

    // Generate quantity fields based on requirements
    const allocatedQuantity = Math.floor(item.physicalQuantity * (Math.random() * 0.5)); // 0-50% of physical
    const quantityDueIn = Math.floor(Math.random() * 100); // 0-100
    const minQuantity = Math.floor(Math.random() * 40) + 10; // 10-50
    const maxQuantity = Math.floor(Math.random() * 400) + 100; // 100-500
    const reorderPoint = Math.floor(minQuantity + (maxQuantity - minQuantity) * 0.3); // Between min and max

    // Generate pricing fields based on requirements
    const lastCostPrice = parseFloat((item.unitPrice * (0.8 + Math.random() * 0.1)).toFixed(2)); // 80-90% of unit price
    const averageCost = parseFloat((item.unitPrice * (0.75 + Math.random() * 0.15)).toFixed(2)); // 75-90% of unit price
    const totalCostOfSales = parseFloat((Math.random() * 490000 + 10000).toFixed(2)); // £10,000 - £500,000
    const totalValueOfSales = parseFloat((totalCostOfSales * (1.2 + Math.random() * 0.3)).toFixed(2)); // 20-50% margin

    // Generate analysis codes
    const purchaseAnalysisCodes = ["PUR-001", "PUR-ELC", "PUR-FUR", "PUR-OFF", "PUR-RAW", "PUR-TOL", "PUR-SAF"];
    const salesAnalysisCodes = ["SAL-RET", "SAL-WHL", "SAL-ONL", "SAL-EXP", "SAL-DOM"];
    const movementInCodes = ["MI-STD", "MI-RET", "MI-TRF", "MI-ADJ", "MI-PUR"];
    const movementOutCodes = ["MO-SAL", "MO-ADJ", "MO-TRF", "MO-WAS", "MO-RET"];

    const purchaseAnalysisCode = purchaseAnalysisCodes[Math.floor(Math.random() * purchaseAnalysisCodes.length)];
    const salesAnalysisCode = salesAnalysisCodes[Math.floor(Math.random() * salesAnalysisCodes.length)];
    const movementInAnalysis = movementInCodes[Math.floor(Math.random() * movementInCodes.length)];
    const movementOutAnalysis = movementOutCodes[Math.floor(Math.random() * movementOutCodes.length)];

    // Generate ABC classification (20% A, 30% B, 50% C)
    const rand = Math.random();
    const abcClassification: "A" | "B" | "C" = rand < 0.2 ? "A" : rand < 0.5 ? "B" : "C";

    // Generate stock turn rate (0.5 - 12.0)
    const stockTurnRate = parseFloat((Math.random() * 11.5 + 0.5).toFixed(1));

    // Generate supplier data
    const suppliers = [
      "ABC Electronics Ltd",
      "Global Furniture Co",
      "Office Depot Inc",
      "TechSupply Solutions",
      "Industrial Materials Group",
      "Premier Office Supplies",
      "BuildMart Wholesale",
      "Quality Tools & Equipment",
      "SafetyFirst Supplies",
      "PackagePro Distributors",
      "MetalWorks International",
      "Furniture Direct Ltd",
      "ElectroTech Suppliers",
      "Office Essentials Co",
      "Hardware & More Inc",
    ];

    // 80% of items have a supplier
    const hasSupplier = Math.random() < 0.8;
    const supplier = hasSupplier ? suppliers[Math.floor(Math.random() * suppliers.length)] : undefined;
    const leadTimeDays = hasSupplier ? Math.floor(Math.random() * 30) + 1 : undefined; // 1-30 days

    // Generate purchase date (last 6 months or null)
    const hasPurchaseHistory = hasSupplier && Math.random() < 0.85;
    let lastPurchaseDate: Date | null = null;
    if (hasPurchaseHistory) {
      const purchaseDaysAgo = Math.floor(Math.random() * 180); // 0-180 days (6 months)
      lastPurchaseDate = new Date();
      lastPurchaseDate.setDate(lastPurchaseDate.getDate() - purchaseDaysAgo);
    }

    // Generate sale date (last 3 months or null)
    const hasSaleHistory = Math.random() < 0.9;
    let lastSaleDate: Date | null = null;
    if (hasSaleHistory) {
      const saleDaysAgo = Math.floor(Math.random() * 90); // 0-90 days (3 months)
      lastSaleDate = new Date();
      lastSaleDate.setDate(lastSaleDate.getDate() - saleDaysAgo);
    }

    // Generate supplier contact email
    const supplierContact = hasSupplier ? `orders@${supplier?.toLowerCase().replace(/[^a-z0-9]/g, "")}.com` : undefined;

    // Generate purchase history data
    const lastPurchaseQuantity = hasPurchaseHistory ? Math.floor(Math.random() * 190) + 10 : undefined; // 10-200
    const lastPurchasePrice = hasPurchaseHistory
      ? parseFloat((lastCostPrice * (0.95 + Math.random() * 0.1)).toFixed(2))
      : undefined;

    // Generate sales history data
    const lastSaleQuantity = hasSaleHistory ? Math.floor(Math.random() * 50) + 1 : undefined; // 1-50
    const lastSalePrice = hasSaleHistory
      ? parseFloat((item.unitPrice * (0.95 + Math.random() * 0.1)).toFixed(2))
      : undefined;

    // Generate supplier performance metrics
    const onTimeDeliveryRate = hasSupplier ? parseFloat((85 + Math.random() * 13).toFixed(1)) : undefined; // 85-98%
    const averageLeadTime =
      hasSupplier && leadTimeDays ? Math.floor(leadTimeDays + (Math.random() * 4 - 2)) : undefined; // leadTimeDays ± 2
    const totalOrdersYTD = hasSupplier ? Math.floor(Math.random() * 46) + 5 : undefined; // 5-50

    // Map old location string to warehouse code and location code
    const locationMapping: { [key: string]: { warehouseCode: string; locationCode: string } } = {
      "Warehouse A": { warehouseCode: "WH-A", locationCode: "A01-R01-S01-B01" },
      "Warehouse B": { warehouseCode: "WH-B", locationCode: "B01-R01-S01-B01" },
      "Warehouse C": { warehouseCode: "WH-C", locationCode: "C01-MAIN" },
      "Retail Store": { warehouseCode: "RS", locationCode: "RS-MAIN" },
      "Distribution Center": { warehouseCode: "DC", locationCode: "DC-RCV-01" },
      "Storage Room": { warehouseCode: "SR", locationCode: "SR-MAIN" },
    };

    const mappedLocation = locationMapping[item.location] || { warehouseCode: "WH-A", locationCode: "A01-R01-S01-B01" };

    // Assign UOM based on category
    const uomMapping: { [key: string]: "Each" | "Box/Carton" | "Kg" | "g" | "Litre" | "Metre" | "Pallet" } = {
      Electronics: "Each",
      Furniture: "Each",
      "Office Supplies": "Box/Carton",
      "Raw Materials": "Kg",
      Tools: "Each",
      "Safety Equipment": "Each",
      Packaging: "Box/Carton",
    };
    const unitOfMeasure = uomMapping[item.category] || "Each";

    return {
      code: item.code,
      description: item.description,
      category: item.category,
      unitOfMeasure,
      physicalQuantity: item.physicalQuantity,
      unitPrice: item.unitPrice,
      inactive: item.inactive,
      barcode: item.barcode,
      warehouseCode: mappedLocation.warehouseCode,
      locationCode: mappedLocation.locationCode,
      value: item.physicalQuantity * item.unitPrice,
      lastMovementDate,
      allocatedQuantity,
      quantityDueIn,
      minQuantity,
      maxQuantity,
      reorderPoint,
      reorderQuantity: Math.floor(Math.random() * 100) + 50, // 50-150
      lastCostPrice,
      averageCost,
      totalCostOfSales,
      totalValueOfSales,
      purchaseAnalysisCode,
      salesAnalysisCode,
      movementInAnalysis,
      movementOutAnalysis,
      abcClassification,
      stockTurnRate,
      supplier,
      leadTimeDays,
      lastPurchaseDate,
      lastSaleDate,
      supplierContact,
      lastPurchaseQuantity,
      lastPurchasePrice,
      lastSaleQuantity,
      lastSalePrice,
      onTimeDeliveryRate,
      averageLeadTime,
      totalOrdersYTD,
      transactionCount: getTransactionCount(item.code),
    };
  });
};

// Use imported mockStockItems which includes relationship data (alternativeItems, linkedItems)  
const mockStockItems = mockStockItemsFromData;

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
};

// Format date - handles invalid dates gracefully
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return "-";

  const d = typeof date === "string" ? new Date(date) : date;

  // Guard against invalid Date objects
  if (Number.isNaN(d.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
};

export function StockItemsPage() {
  const { navigateTo, pageParams } = useNavigation();
  const { valuationMethod, getValuationMethodDisplayName } = useStockCosting();

  // Mode context for demo/live toggle (screen-specific) - must be before hooks that depend on it
  const { setMode, isDemo, isLive } = useScreenMode("stock-items");
  const isLiveMode = isLive; // Alias for compatibility with existing code

  // Only fetch custom fields in live mode to avoid backend calls in demo mode
  const { definitions: customFieldDefinitions } = useCustomFields({ enabled: isLiveMode });
  const {
    stockItems: backendStockItems,
    pageInfo: backendPageInfo,
    isLoading: isBackendLoading,
    fetchStockItems,
    fetchStockItemByCode,
    updateStockItem,
    createStockItem,
    deleteStockItems,
  } = useStockItems();

  // Stock value hook for live mode - to get total stock items count
  const { stockValue: liveStockValue, fetchStockValue } = useWarehouseStockValue();

  // Get level 1 label from warehouse hierarchy config for location column
  const { getLevelDisplayName } = useWarehouseHierarchyConfig();
  const level1Label = getLevelDisplayName(1);

  // Cursor-based pagination state for live mode
  // Using a ref to avoid re-fetches when cursors are stored
  const cursorsRef = useRef<string[]>([]);

  // Combine static columns with custom field columns and dynamic location label
  const allColumnsWithCustomFields = useMemo(() => {
    const customFieldColumns: ColumnDefinition[] = customFieldDefinitions.map((def) => ({
      id: `customField_${def.customFieldDefinitionID}`,
      name: def.fieldLabel,
      description: `Custom field: ${def.fieldLabel}`,
    }));
    // Update location column name with dynamic level1 label
    const columnsWithDynamicLabels = ALL_COLUMNS.map((col) =>
      col.id === "location" ? { ...col, name: level1Label } : col,
    );
    return [...columnsWithDynamicLabels, ...customFieldColumns];
  }, [customFieldDefinitions, level1Label]);

  // Generate filter fields based on the columns in the grid (only filterable columns)
  // This ensures advanced filters only show fields that are actually in the grid
  const gridFilterFields = useMemo((): FilterField[] => {
    // Define field types for each column - only include columns that are in ALL_COLUMNS
    const columnTypeMap: Record<string, { type: FilterField["type"]; options?: { value: string; label: string }[] }> = {
      code: { type: "text" },
      description: { type: "text" },
      category: { type: "text" },
      unitOfMeasure: { type: "text" },
      location: {
        type: "select",
        options: [
          { value: "Single", label: "Single" },
          { value: "Multiple", label: "Multiple" },
        ],
      },
      physicalQuantity: { type: "number" },
      allocatedQuantity: { type: "number" },
      freeQuantity: { type: "number" },
      quantityDueIn: { type: "number" },
      unitPrice: { type: "number" },
      value: { type: "number" },
      totalCostOfSales: { type: "number" },
      totalValueOfSales: { type: "number" },
      status: {
        type: "select",
        options: [
          { value: "Active", label: "Active" },
          { value: "Inactive", label: "Inactive" },
        ],
      },
      weight: { type: "number" },
      barcode: { type: "text" },
      binNumber: { type: "text" },
      minQuantity: { type: "number" },
      maxQuantity: { type: "number" },
      reorderPoint: { type: "number" },
      reorderQuantity: { type: "number" },
      lastCostPrice: { type: "number" },
      averageCost: { type: "number" },
      grossMarginPercent: { type: "number" },
      purchaseAnalysisCode: { type: "text" },
      salesAnalysisCode: { type: "text" },
      movementInAnalysis: { type: "text" },
      movementOutAnalysis: { type: "text" },
      abcClassification: { type: "text" },
      stockTurnRate: { type: "number" },
      supplier: { type: "text" },
      leadTimeDays: { type: "number" },
      lastPurchaseDate: { type: "date" },
      lastSaleDate: { type: "date" },
      lastMovementDate: { type: "date" },
    };

    // Generate filter fields from ALL_COLUMNS (the grid columns)
    const filterFields: FilterField[] = ALL_COLUMNS.filter((col) => columnTypeMap[col.id]) // Only include columns with defined types
      .map((col) => {
        const typeInfo = columnTypeMap[col.id];
        // Use dynamic label for location column
        const label = col.id === "location" ? level1Label : col.name;
        return {
          id: col.id,
          label,
          type: typeInfo.type,
          ...(typeInfo.options && { options: typeInfo.options }),
        };
      });

    return filterFields;
  }, [level1Label]);
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("stock-items");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("code");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [isStockMovementDialogOpen, setIsStockMovementDialogOpen] = useState(false);
  const [isStockTakeDialogOpen, setIsStockTakeDialogOpen] = useState(false);
  const [isMovementTypeDialogOpen, setIsMovementTypeDialogOpen] = useState(false);
  const [preparedMovementItems, setPreparedMovementItems] = useState<PreparedStockMovementItem[]>([]);
  const [duplicateValidationResult, setDuplicateValidationResult] = useState<DuplicateValidationResult>({
    canProceed: false,
    items: [],
    warnings: [],
    errors: [],
  });
  const [stockTakeValidationResult, setStockTakeValidationResult] = useState<StockTakeStartValidationResult>({
    canProceed: false,
    validItems: [],
    excludedItems: [],
    warnings: [],
  });
  const [stockItems, setStockItems] = useState<StockItem[]>(mockStockItems);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDrawerItem, setSelectedDrawerItem] = useState<StockItem | null>(null);
  const [originalDrawerItem, setOriginalDrawerItem] = useState<StockItem | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [pendingRowSelection, setPendingRowSelection] = useState<StockItem | null>(null);

  // Advanced Filters state
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [activeFilterRows, setActiveFilterRows] = useState<FilterRow[]>([]);
  const [activeFilterLogic, setActiveFilterLogic] = useState<FilterLogic[]>([]);
  const [activeFilterChips, setActiveFilterChips] = useState<ActiveFilterChip[]>([]);

  // Configure Columns state
  const [isConfigureColumnsModalOpen, setIsConfigureColumnsModalOpen] = useState(false);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig>({
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    columnOrder: DEFAULT_VISIBLE_COLUMNS,
  });

  // Load column configuration from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem("stockItemsColumnConfig");
      if (saved) {
        const parsed = JSON.parse(saved) as ColumnConfig;
        if (Array.isArray(parsed.visibleColumns) && Array.isArray(parsed.columnOrder)) {
          setColumnConfig(parsed);
        }
      }
    } catch {
      // ignore malformed data
    }
  }, []);

  // Initialize locations with stock data on mount
  useEffect(() => {
    // Import and initialize locations with actual stock data
    import("@/data/initializeLocationStock").then(({ initializeLocationsWithStockData }) => {
      initializeLocationsWithStockData(mockStockItems);
    });
  }, []);

  // Loading states
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Ref to store the custom fields save handler
  const customFieldsSaveHandlerRef = useRef<(() => Promise<boolean>) | null>(null);

  // Apply initial filters from navigation params
  useEffect(() => {
    if (pageParams?.filters && Array.isArray(pageParams.filters)) {
      setActiveFilterRows(pageParams.filters);
      setActiveFilterLogic(pageParams.logic || []);
      // Generate filter chips for display
      const chips = generateFilterChips(pageParams.filters, pageParams.logic || []);
      setActiveFilterChips(chips);
    }
  }, [pageParams]);

  // Debounce search query with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  // Track previous filter values to detect changes and reset pagination
  const prevFiltersRef = useRef<{
    searchQuery: string;
    sortColumn: string;
    sortDirection: string;
  } | null>(null);

  // Fetch stock items when in live mode with server-side pagination, filtering, and sorting
  useEffect(() => {
    if (isLiveMode) {
      // Check if filter-related values have changed (not pagination)
      const currentFilters = {
        searchQuery: debouncedSearchQuery,
        sortColumn,
        sortDirection,
      };

      const filtersChanged =
        prevFiltersRef.current !== null &&
        (prevFiltersRef.current.searchQuery !== currentFilters.searchQuery ||
          prevFiltersRef.current.sortColumn !== currentFilters.sortColumn ||
          prevFiltersRef.current.sortDirection !== currentFilters.sortDirection);

      // Update the ref with current values
      prevFiltersRef.current = currentFilters;

      // If filters changed, reset pagination and fetch from the beginning
      let effectivePageIndex = currentPage - 1;
      if (filtersChanged) {
        cursorsRef.current = [];
        setCurrentPage(1);
        effectivePageIndex = 0;
      }

      // Build sort input based on current sort state (using field/order structure)
      let sortInput: StockItemSortInput | undefined;
      const order = sortDirection === "asc" ? "Ascending" : "Descending";

      if (sortColumn === "code") {
        sortInput = { field: "StockCode", order };
      } else if (sortColumn === "description") {
        sortInput = { field: "Description", order };
      } else if (sortColumn === "category") {
        sortInput = { field: "Category", order };
      } else if (sortColumn === "physicalQuantity") {
        sortInput = { field: "PhysicalQuantity", order };
      } else if (sortColumn === "unitPrice") {
        sortInput = { field: "UnitPrice", order };
      } else if (sortColumn === "totalValue") {
        sortInput = { field: "TotalValue", order };
      } else if (sortColumn === "lastMovementDate") {
        sortInput = { field: "LastMovementDate", order };
      }

      // Note: Search filtering is done client-side to support OR logic across
      // code, description, and barcode fields (matching demo mode behavior)
      const filterInput: StockItemFilterInput = {};

      // Get the cursor for the current page from the ref
      const cursors = cursorsRef.current;
      const after = effectivePageIndex > 0 ? cursors[effectivePageIndex - 1] : undefined;

      fetchStockItems({
        first: itemsPerPage,
        after,
        filter: Object.keys(filterInput).length > 0 ? filterInput : undefined,
        sort: sortInput,
      }).then(({ pageInfo }) => {
        // Store the end cursor for next page navigation
        // Only store if we're at the frontier (fetching a page we haven't fetched before)
        if (pageInfo?.endCursor && effectivePageIndex === cursorsRef.current.length) {
          cursorsRef.current = [...cursorsRef.current, pageInfo.endCursor];
        }
      });

      // Fetch total stock items count from dashboardSummary (without warehouseCode to get all items)
      fetchStockValue();
    }
  }, [
    isLiveMode,
    debouncedSearchQuery,
    sortColumn,
    sortDirection,
    itemsPerPage,
    currentPage,
    fetchStockItems,
    fetchStockValue,
  ]);

  // Get the current data source based on mode
  const currentDataSource = useMemo(() => {
    return isLiveMode ? backendStockItems : stockItems;
  }, [isLiveMode, backendStockItems, stockItems]);

  // Handle mode toggle - uses shared ModeProvider
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
      setCurrentPage(1);
      setSelectedItems(new Set());
      cursorsRef.current = [];
    },
    [setMode],
  );

  // Filter items based on debounced search query
  const filteredItems = useMemo(() => {
    // In live mode, apply search and advanced filters client-side
    if (isLiveMode) {
      let items = backendStockItems;

      // Apply advanced filters client-side
      if (activeFilterRows.length > 0) {
        items = applyFilters(items, activeFilterRows, activeFilterLogic);
      }

      // Apply search query client-side to support OR logic across code and description
      if (debouncedSearchQuery.trim()) {
        const query = debouncedSearchQuery.toLowerCase();
        items = items.filter((item) => {
          return item.code.toLowerCase().includes(query) || item.description.toLowerCase().includes(query);
        });
      }

      return items;
    }

    // Demo mode: filter mock data locally
    let items = currentDataSource;

    // Apply advanced filters first
    if (activeFilterRows.length > 0) {
      items = applyFilters(items, activeFilterRows, activeFilterLogic);
    }

    // Then apply search query
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      items = items.filter((item) => {
        return item.code.toLowerCase().includes(query) || item.description.toLowerCase().includes(query);
      });
    }

    return items;
  }, [isLiveMode, backendStockItems, debouncedSearchQuery, activeFilterRows, activeFilterLogic, currentDataSource]);

  // Sort filtered items (for demo mode - live mode sorting is done on server)
  const sortedItems = useMemo(() => {
    // In live mode, sorting is done on the server
    if (isLiveMode) {
      return filteredItems;
    }

    // Demo mode: sort locally
    const items = [...filteredItems];

    items.sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (sortColumn) {
        case "code":
          aValue = a.code;
          bValue = b.code;
          break;
        case "description":
          aValue = a.description;
          bValue = b.description;
          break;
        case "category":
          aValue = a.category;
          bValue = b.category;
          break;
        case "type":
          aValue = a.type || "Purchased";
          bValue = b.type || "Purchased";
          break;
        case "physicalQuantity":
          aValue = a.physicalQuantity;
          bValue = b.physicalQuantity;
          break;
        case "unitPrice":
          aValue = a.unitPrice;
          bValue = b.unitPrice;
          break;
        case "value":
          aValue = a.value;
          bValue = b.value;
          break;
        case "lastMovementDate":
          aValue = a.lastMovementDate.getTime();
          bValue = b.lastMovementDate.getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return items;
  }, [isLiveMode, filteredItems, sortColumn, sortDirection]);

  // Paginate sorted items (for demo mode - live mode pagination is done on server)
  const paginatedItems = useMemo(() => {
    // In live mode, pagination is done on the server
    if (isLiveMode) {
      return sortedItems;
    }

    // Demo mode: paginate locally
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedItems.slice(startIndex, endIndex);
  }, [isLiveMode, sortedItems, currentPage, itemsPerPage]);

  // Calculate pagination values
  // In live mode, use totalStockItems from dashboardSummary to calculate total pages
  const totalStockItemsCount = isLiveMode ? (liveStockValue?.totalStockItems ?? 0) : sortedItems.length;
  const totalPages = isLiveMode
    ? liveStockValue?.totalStockItems
      ? Math.ceil(liveStockValue.totalStockItems / itemsPerPage)
      : backendPageInfo?.hasNextPage
        ? currentPage + 1
        : currentPage
    : Math.ceil(sortedItems.length / itemsPerPage);
  const startItem = totalStockItemsCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = isLiveMode
    ? Math.min(currentPage * itemsPerPage, totalStockItemsCount)
    : Math.min(currentPage * itemsPerPage, sortedItems.length);

  // Handle column header click for sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column with ascending direction
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Render sort icon
  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 inline ml-1" />
    );
  };

  // Clear search handler
  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all items on current page
      const newSelection = new Set(selectedItems);
      paginatedItems.forEach((item) => newSelection.add(item.code));
      setSelectedItems(newSelection);
    } else {
      // Deselect all items on current page
      const newSelection = new Set(selectedItems);
      paginatedItems.forEach((item) => newSelection.delete(item.code));
      setSelectedItems(newSelection);
    }
  };

  const handleSelectItem = (code: string, checked: boolean) => {
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(code);
    } else {
      newSelection.delete(code);
    }
    setSelectedItems(newSelection);
  };

  const handleClearSelection = () => {
    setSelectedItems(new Set());
  };

  // Check if all items on current page are selected
  const allPageItemsSelected =
    paginatedItems.length > 0 && paginatedItems.every((item) => selectedItems.has(item.code));
  const somePageItemsSelected = paginatedItems.some((item) => selectedItems.has(item.code)) && !allPageItemsSelected;

  // Get selected items data
  const selectedItemsData = useMemo(() => {
    return currentDataSource.filter((item) => selectedItems.has(item.code));
  }, [selectedItems, currentDataSource]);

  // Bulk action handlers
  const handleDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async (itemCodesToDelete: string[]) => {
    if (isLiveMode) {
      // Live mode: Call the API to delete the stock items from the database
      // Get the stockItemIDs for the items to delete
      const itemsToDelete = currentDataSource.filter((item) => itemCodesToDelete.includes(item.code));
      const stockItemIds = itemsToDelete
        .map((item) => item.stockItemID)
        .filter((id): id is number => id !== undefined && id !== null);

      if (stockItemIds.length === 0) {
        toast.error("No valid stock item IDs found for deletion");
        return;
      }

      const result = await deleteStockItems(stockItemIds);
      if (result.success) {
        toast.success(`${itemCodesToDelete.length} item(s) deleted successfully`);
        // Reset pagination before refreshing to avoid stale cursor issues
        cursorsRef.current = [];
        setCurrentPage(1);
        // Refresh the stock items list to reflect the deletion
        await fetchStockItems();
      } else {
        toast.error(`Failed to delete items: ${result.error}`);
        return;
      }
    } else {
      // Demo mode: Just remove from local state
      const updatedItems = stockItems.filter((item) => !itemCodesToDelete.includes(item.code));
      setStockItems(updatedItems);
      toast.success(`${itemCodesToDelete.length} item(s) deleted successfully`);
    }

    // Clear selection
    setSelectedItems(new Set());
  };

  const handleStartStockMovement = () => {
    setIsStockMovementDialogOpen(true);
  };

  const handleStartStockTake = () => {
    // Validate and prepare items for stock take
    const { validation, preparedItems } = validateAndPrepareStockTake(selectedItemsData);

    // Store validation result
    setStockTakeValidationResult(validation);

    // Open dialog
    setIsStockTakeDialogOpen(true);
  };

  const confirmStockMovement = (preparedItems: PreparedStockMovementItem[]) => {
    // Store prepared items and show movement type selection dialog
    setPreparedMovementItems(preparedItems);
    setIsStockMovementDialogOpen(false);
    setIsMovementTypeDialogOpen(true);
  };

  const handleMovementTypeSelection = (movementType: string) => {
    // Stock Transfer uses the wizard flow
    if (movementType === "Transfer Between Locations") {
      navigateTo("stock-transfers/create/step1", {
        prepopulatedItems: preparedMovementItems,
      });
    } else {
      // Other movement types use the unified stock movement page
      navigateTo("add-movement", {
        prepopulatedItems: preparedMovementItems,
        type: movementType,
      });
    }

    // Clear selection and close dialog
    setSelectedItems(new Set());
    setIsMovementTypeDialogOpen(false);
    setPreparedMovementItems([]);
  };

  const confirmStockTake = () => {
    // Prepare items for stock take
    const { preparedItems } = validateAndPrepareStockTake(selectedItemsData);

    // Navigate to partial stock take setup page with pre-populated items
    navigateTo("stock-takes/targeted/setup", {
      prepopulatedItems: preparedItems,
    });

    // Clear selection
    setSelectedItems(new Set());
  };

  const handleDuplicate = () => {
    // Validate selected items for duplication
    const allStockCodes = stockItems.map((item) => item.code);
    const validationResult = validateDuplication(selectedItemsData, allStockCodes);

    setDuplicateValidationResult(validationResult);
    setIsDuplicateDialogOpen(true);
  };

  // Confirm duplicate action
  const confirmDuplicate = async (items: DuplicateItem[]) => {
    if (items.length === 0) return;

    const newItems: StockItem[] = [];
    const failedItems: { code: string; error?: string }[] = [];

    // Create duplicate items
    for (const item of items) {
      const duplicateData = prepareDuplicateData(item.original, item.newCode, item.isActive);

      // Create new stock item with generated data
      const newItem: StockItem = {
        ...item.original,
        ...duplicateData,
        // Ensure required fields are set
        code: item.newCode,
        barcode: "",
        physicalQuantity: 0,
        allocatedQuantity: 0,
        quantityDueIn: 0,
        value: 0,
        totalCostOfSales: 0,
        totalValueOfSales: 0,
        inactive: !item.isActive,
        lastMovementDate: new Date(),
        lastPurchaseDate: null,
        lastSaleDate: null,
      } as StockItem;

      if (isLiveMode) {
        // Live mode: Call the API to create the stock item in the database
        const result = await createStockItem(newItem);
        if (result.success && result.id) {
          newItem.stockItemID = result.id;
          newItems.push(newItem);
        } else {
          failedItems.push({ code: item.newCode, error: result.error });
        }
      } else {
        // Demo mode: Just add to local state
        newItems.push(newItem);
      }
    }

    // Handle results
    if (failedItems.length > 0) {
      // Show the actual error message from the backend if available
      const firstError = failedItems[0].error;
      const errorMessage = firstError
        ? `Failed to duplicate: ${firstError}`
        : `Failed to duplicate ${failedItems.length} item(s): ${failedItems.map((f) => f.code).join(", ")}`;
      toast.error(errorMessage);
    }

    if (newItems.length > 0) {
      if (!isLiveMode) {
        // Demo mode: Add new items to stock items locally
        const updatedItems = [...stockItems, ...newItems];
        setStockItems(updatedItems);
      }

      // Show success toast
      const count = newItems.length;
      toast.success(`${count} item${count > 1 ? "s" : ""} duplicated successfully`);

      // Close dialog and clear selection
      setIsDuplicateDialogOpen(false);
      setSelectedItems(new Set());

      // In live mode, refresh the stock items list to get the new items
      if (isLiveMode) {
        // Reset pagination before refreshing to avoid stale cursor issues
        cursorsRef.current = [];
        setCurrentPage(1);
        await fetchStockItems();
      } else {
        // Navigate to the first new item (demo mode only)
        const firstNewCode = newItems[0].code;
        const updatedItems = [...stockItems, ...newItems];
        const allSortedItems = [...updatedItems].sort((a, b) => a.code.localeCompare(b.code));
        const newItemIndex = allSortedItems.findIndex((item) => item.code === firstNewCode);
        const newItemPage = Math.floor(newItemIndex / itemsPerPage) + 1;

        // Navigate to the page containing the new item
        setCurrentPage(newItemPage);

        // Scroll to the new item after a short delay to allow page change
        setTimeout(() => {
          const tableRows = document.querySelectorAll("tbody tr");
          const newItemRowIndex = newItemIndex % itemsPerPage;
          const newItemRow = tableRows[newItemRowIndex] as HTMLElement;

          if (newItemRow) {
            newItemRow.scrollIntoView({ behavior: "smooth", block: "center" });
            // Highlight the row briefly
            newItemRow.style.backgroundColor = "hsl(var(--access-soft-teal))";
            setTimeout(() => {
              newItemRow.style.backgroundColor = "";
            }, 2000);
          }
        }, 100);
      }
    } else {
      // All items failed
      setIsDuplicateDialogOpen(false);
      setSelectedItems(new Set());
    }
  };

  // Drawer handlers
  const handleRowClick = async (item: StockItem) => {
    // Check if there are unsaved changes in the current drawer
    if (hasUnsavedChanges && selectedDrawerItem) {
      setPendingRowSelection(item);
      setIsDiscardDialogOpen(true);
      return;
    }

    // In live mode, fetch the complete item data including settings
    if (isLiveMode) {
      // Open drawer immediately with basic item data for responsiveness
      const clonedItem = JSON.parse(JSON.stringify(item));
      setSelectedDrawerItem(clonedItem);
      setOriginalDrawerItem(clonedItem);
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      setIsDrawerOpen(true);

      // Fetch complete item data with settings in the background
      const fullItem = await fetchStockItemByCode(item.code);
      if (fullItem) {
        setSelectedDrawerItem(fullItem);
        setOriginalDrawerItem(JSON.parse(JSON.stringify(fullItem)));
      }
    } else {
      // Demo mode: use the item data directly
      const clonedItem = JSON.parse(JSON.stringify(item));
      setSelectedDrawerItem(clonedItem);
      setOriginalDrawerItem(clonedItem);
      setIsEditMode(false);
      setHasUnsavedChanges(false);
      setIsDrawerOpen(true);
    }
  };

  const handleDrawerClose = (open: boolean) => {
    if (!open && hasUnsavedChanges) {
      // Show discard confirmation dialog
      setIsDiscardDialogOpen(true);
      return;
    }

    setIsDrawerOpen(open);
    if (!open) {
      setSelectedDrawerItem(null);
      setOriginalDrawerItem(null);
      setIsEditMode(false);
      setHasUnsavedChanges(false);
    }
  };

  const handleConfirmDiscard = async () => {
    setIsDiscardDialogOpen(false);
    setHasUnsavedChanges(false);

    // If there's a pending row selection, open that item
    if (pendingRowSelection) {
      const clonedItem = JSON.parse(JSON.stringify(pendingRowSelection));
      setSelectedDrawerItem(clonedItem);
      setOriginalDrawerItem(clonedItem);
      setIsEditMode(false);
      setPendingRowSelection(null);

      // In live mode, fetch the complete item data including settings
      if (isLiveMode) {
        const fullItem = await fetchStockItemByCode(pendingRowSelection.code);
        if (fullItem) {
          setSelectedDrawerItem(fullItem);
          setOriginalDrawerItem(JSON.parse(JSON.stringify(fullItem)));
        }
      }
    } else {
      // Otherwise just close the drawer
      setIsDrawerOpen(false);
      setSelectedDrawerItem(null);
      setOriginalDrawerItem(null);
      setIsEditMode(false);
    }
  };

  const handleCancelDiscard = () => {
    setIsDiscardDialogOpen(false);
    setPendingRowSelection(null);
  };

  const handleOpenInFullView = () => {
    if (!selectedDrawerItem) return;

    // Close the drawer
    setIsDrawerOpen(false);
    setSelectedDrawerItem(null);
    setOriginalDrawerItem(null);
    setIsEditMode(false);
    setHasUnsavedChanges(false);

    // Navigate to the detail page with the item code
    navigateTo("stock-item-detail", { itemCode: selectedDrawerItem.code });
  };

  const handleNavigateToTransaction = (type: string, reference: string, transactionId: string) => {
    // Map transaction types to page routes
    const typeToPageMap: Record<string, string> = {
      "purchase-order": "purchase-orders",
      "sales-order": "sales-orders",
      "stock-adjustment": "stock-movements",
      "stock-transfer": "stock-movements",
      "customer-return": "stock-movements",
      "supplier-return": "stock-movements",
    };

    const targetPage = typeToPageMap[type];
    if (targetPage) {
      // Navigate to the target page with the reference number (not the transaction ID)
      navigateTo(targetPage, { transactionId: reference });
      toast.info(`Navigating to ${reference}...`);
    } else {
      toast.error("Unknown transaction type");
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedDrawerItem) return;

    try {
      // Prepare the updated item with recalculated derived fields
      const updatedItem = {
        ...selectedDrawerItem,
        value: selectedDrawerItem.physicalQuantity * selectedDrawerItem.unitPrice,
      };

      if (isLiveMode) {
        // In live mode, save to backend via GraphQL mutation
        const success = await updateStockItem(updatedItem);
        if (!success) {
          // Error toast is already shown by updateStockItem
          return;
        }

        // Save custom field values if handler is registered (before refreshing the list)
        if (customFieldsSaveHandlerRef.current) {
          const customFieldsSaved = await customFieldsSaveHandlerRef.current();
          if (!customFieldsSaved) {
            toast.error("Failed to save custom field values");
            return;
          }
        }

        // Refresh the stock items list from backend to ensure grid is updated with all changes including custom fields
        await fetchStockItems();
      } else {
        // In demo mode, just update local state
        await new Promise((resolve) => setTimeout(resolve, 500));
        const updatedItems = stockItems.map((item) => (item.code === selectedDrawerItem.code ? updatedItem : item));
        setStockItems(updatedItems);
      }

      // Reset change tracking
      setHasUnsavedChanges(false);
      setOriginalDrawerItem(JSON.parse(JSON.stringify(updatedItem)));

      // Show success toast
      toast.success("Stock item updated successfully");

      // Close drawer
      setIsDrawerOpen(false);
      setSelectedDrawerItem(null);
      setOriginalDrawerItem(null);
      setIsEditMode(false);
    } catch (error) {
      toast.error("Failed to save changes");
    }
  };

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isDrawerOpen]);

  // Advanced Filters handlers
  const handleApplyFilters = (filterRows: FilterRow[], filterLogic: FilterLogic[]) => {
    setActiveFilterRows(filterRows);
    setActiveFilterLogic(filterLogic);
    setActiveFilterChips(generateFilterChips(filterRows, filterLogic));
    setIsFiltersModalOpen(false);
    setCurrentPage(1); // Reset to first page when filters change
    toast.success(`${filterRows.length} filter(s) applied successfully`);
  };

  const handleRemoveFilterChip = (index: number) => {
    const updatedFilterRows = activeFilterRows.filter((_, i) => i !== index);
    const updatedFilterLogic = activeFilterLogic.filter((_, i) => i !== index);

    setActiveFilterRows(updatedFilterRows);
    setActiveFilterLogic(updatedFilterLogic);
    setActiveFilterChips(generateFilterChips(updatedFilterRows, updatedFilterLogic));
    setCurrentPage(1);

    if (updatedFilterRows.length === 0) {
      toast.success("All filters cleared");
    } else {
      toast.success("Filter removed");
    }
  };

  const handleClearAllFilters = () => {
    setActiveFilterRows([]);
    setActiveFilterLogic([]);
    setActiveFilterChips([]);
    setCurrentPage(1);
    toast.success("All filters cleared");
  };

  // Get visible columns in the correct order based on columnConfig
  const getOrderedVisibleColumns = useCallback(() => {
    return columnConfig.columnOrder.filter((colId) => columnConfig.visibleColumns.includes(colId));
  }, [columnConfig]);

  // Check if a column is sortable
  const isSortableColumn = (columnId: string): columnId is SortColumn => {
    return ["code", "description", "category", "type", "physicalQuantity", "unitPrice", "value", "lastMovementDate"].includes(
      columnId,
    );
  };

  // Render table header cell based on column ID
  const renderTableHeaderCell = (columnId: string) => {
    const column = allColumnsWithCustomFields.find((col) => col.id === columnId);
    if (!column) return null;

    // Check if it's a custom field column
    if (columnId.startsWith("customField_")) {
      return (
        <TableHead key={`header_${columnId}`} className="font-semibold">
          {column.name}
        </TableHead>
      );
    }

    // Define column-specific styles
    const rightAlignedColumns = [
      "physicalQuantity",
      "unitPrice",
      "value",
      "allocatedQuantity",
      "freeQuantity",
      "quantityDueIn",
      "totalCostOfSales",
      "totalValueOfSales",
      "weight",
      "minQuantity",
      "maxQuantity",
      "reorderPoint",
      "reorderQuantity",
      "lastCostPrice",
      "averageCost",
      "grossMarginPercent",
      "stockTurnRate",
      "leadTimeDays",
    ];
    const minWidthColumns: Record<string, string> = {
      description: "min-w-[250px]",
    };

    const isRightAligned = rightAlignedColumns.includes(columnId);
    const minWidth = minWidthColumns[columnId] || "";
    const isSortable = isSortableColumn(columnId);

    if (isSortable) {
      return (
        <TableHead
          key={`header_${columnId}`}
          className={`font-semibold ${
            isRightAligned ? "text-right" : ""
          } ${minWidth} cursor-pointer hover:bg-muted/50 transition-colors`}
          onClick={() => handleSort(columnId)}
        >
          {column.name} {renderSortIcon(columnId)}
        </TableHead>
      );
    }

    return (
      <TableHead
        key={`header_${columnId}`}
        className={`font-semibold ${isRightAligned ? "text-right" : ""} ${minWidth}`}
      >
        {column.name}
      </TableHead>
    );
  };

  // Render table cell based on column ID and item
  const renderTableCell = (item: StockItem, columnId: string) => {
    // Handle custom field columns
    if (columnId.startsWith("customField_")) {
      const defId = columnId.replace("customField_", "");
      const def = customFieldDefinitions.find((d) => d.customFieldDefinitionID.toString() === defId);
      if (def) {
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-sm">
            {item.customFields?.[def.fieldName] ?? "-"}
          </TableCell>
        );
      }
      return null;
    }

    switch (columnId) {
      case "code":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-medium">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateTo("stock-item-detail", { itemCode: item.code });
              }}
              className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
            >
              {item.code}
            </button>
          </TableCell>
        );
      case "description":
        return <TableCell key={`cell_${columnId}_${item.code}`}>{item.description}</TableCell>;
      case "category":
        return <TableCell key={`cell_${columnId}_${item.code}`}>{item.category || "-"}</TableCell>;
      case "type":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`}>
            <Badge variant="outline" className="text-xs">
              {item.type || "Purchased"}
            </Badge>
          </TableCell>
        );
      case "unitOfMeasure":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`}>
            <Badge variant="outline" className="font-mono text-xs">
              {item.unitOfMeasure || "Each"}
            </Badge>
          </TableCell>
        );
      case "location":
        const hasMultipleLocations = item.locationQuantities && item.locationQuantities.length > 1;
        return (
          <TableCell key={`cell_${columnId}_${item.code}`}>
            <Badge variant={hasMultipleLocations ? "secondary" : "outline"} className="text-xs">
              {hasMultipleLocations ? "Multiple" : "Single"}
            </Badge>
          </TableCell>
        );
      case "physicalQuantity":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.physicalQuantity.toLocaleString()}
          </TableCell>
        );
      case "unitPrice":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {formatCurrency(item.unitPrice)}
          </TableCell>
        );
      case "value":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono font-semibold">
            {formatCurrency(item.value)}
          </TableCell>
        );
      case "status":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`}>
            {item.inactive ? (
              <Badge variant="secondary" className="bg-muted text-muted-foreground">
                Inactive
              </Badge>
            ) : (
              <Badge variant="success" accent="success">
                Active
              </Badge>
            )}
          </TableCell>
        );
      case "lastMovementDate":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {formatDate(item.lastMovementDate)}
          </TableCell>
        );
      case "allocatedQuantity":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.allocatedQuantity.toLocaleString()}
          </TableCell>
        );
      case "freeQuantity":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {(item.physicalQuantity - item.allocatedQuantity).toLocaleString()}
          </TableCell>
        );
      case "quantityDueIn":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.quantityDueIn.toLocaleString()}
          </TableCell>
        );
      case "totalCostOfSales":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {formatCurrency(item.totalCostOfSales)}
          </TableCell>
        );
      case "totalValueOfSales":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {formatCurrency(item.totalValueOfSales)}
          </TableCell>
        );
      case "weight":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.weight != null ? `${item.weight} ${item.weightUnit || "kg"}` : "-"}
          </TableCell>
        );
      case "barcode":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {item.barcode || "-"}
          </TableCell>
        );
      case "binNumber":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {item.binNumber || "-"}
          </TableCell>
        );
      case "minQuantity":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.minQuantity.toLocaleString()}
          </TableCell>
        );
      case "maxQuantity":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.maxQuantity.toLocaleString()}
          </TableCell>
        );
      case "reorderPoint":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.reorderPoint.toLocaleString()}
          </TableCell>
        );
      case "reorderQuantity":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.reorderQuantity.toLocaleString()}
          </TableCell>
        );
      case "lastCostPrice":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {formatCurrency(item.lastCostPrice)}
          </TableCell>
        );
      case "averageCost":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {formatCurrency(item.averageCost)}
          </TableCell>
        );
      case "grossMarginPercent":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.unitPrice > 0 ? `${(((item.unitPrice - item.averageCost) / item.unitPrice) * 100).toFixed(1)}%` : "-"}
          </TableCell>
        );
      case "purchaseAnalysisCode":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {item.purchaseAnalysisCode || "-"}
          </TableCell>
        );
      case "salesAnalysisCode":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {item.salesAnalysisCode || "-"}
          </TableCell>
        );
      case "movementInAnalysis":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {item.movementInAnalysis || "-"}
          </TableCell>
        );
      case "movementOutAnalysis":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {item.movementOutAnalysis || "-"}
          </TableCell>
        );
      case "abcClassification":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`}>
            <Badge
              variant={
                item.abcClassification === "A" ? "default" : item.abcClassification === "B" ? "secondary" : "outline"
              }
            >
              {item.abcClassification}
            </Badge>
          </TableCell>
        );
      case "stockTurnRate":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.stockTurnRate.toFixed(2)}
          </TableCell>
        );
      case "supplier":
        return <TableCell key={`cell_${columnId}_${item.code}`}>{item.supplier || "-"}</TableCell>;
      case "leadTimeDays":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="text-right font-mono">
            {item.leadTimeDays != null ? item.leadTimeDays : "-"}
          </TableCell>
        );
      case "lastPurchaseDate":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {item.lastPurchaseDate ? formatDate(item.lastPurchaseDate) : "-"}
          </TableCell>
        );
      case "lastSaleDate":
        return (
          <TableCell key={`cell_${columnId}_${item.code}`} className="font-mono text-sm">
            {item.lastSaleDate ? formatDate(item.lastSaleDate) : "-"}
          </TableCell>
        );
      default:
        return null;
    }
  };

  // Export handlers
  const handleExportCurrentViewCSV = async () => {
    if (sortedItems.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      // Simulate processing delay
      await new Promise((resolve) => setTimeout(resolve, sortedItems.length > 100 ? 1000 : 500));

      const visibleColumns = getVisibleColumns(DEFAULT_VISIBLE_COLUMNS, ALL_COLUMNS);
      const preparedData = prepareDataForExport(sortedItems, visibleColumns);
      const filename = `stock-items-current-view-${getCurrentDateForFilename()}.csv`;

      exportToCSV(
        preparedData,
        visibleColumns.map((col) => col.name),
        filename,
      );
      toast.success(`Export completed successfully. ${sortedItems.length} items exported to CSV`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCurrentViewExcel = async () => {
    if (sortedItems.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      // Simulate processing delay
      await new Promise((resolve) => setTimeout(resolve, sortedItems.length > 100 ? 1000 : 500));

      const visibleColumns = getVisibleColumns(DEFAULT_VISIBLE_COLUMNS, ALL_COLUMNS);
      const preparedData = prepareDataForExport(sortedItems, visibleColumns);
      const filename = `stock-items-current-view-${getCurrentDateForFilename()}.xlsx`;

      exportToExcel(
        preparedData,
        visibleColumns.map((col) => col.name),
        filename,
      );
      toast.success(`Export completed successfully. ${sortedItems.length} items exported to Excel`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllDataCSV = async () => {
    if (mockStockItems.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      // Show warning for large datasets
      if (mockStockItems.length > 1000) {
        toast.info("Large export in progress...");
      }

      // Simulate processing delay
      await new Promise((resolve) => setTimeout(resolve, mockStockItems.length > 1000 ? 2000 : 500));

      const allColumns = getAllColumns(ALL_COLUMNS);
      const preparedData = prepareDataForExport(mockStockItems, allColumns);
      const filename = `stock-items-all-data-${getCurrentDateForFilename()}.csv`;

      exportToCSV(
        preparedData,
        allColumns.map((col) => col.name),
        filename,
      );
      toast.success(`Export completed successfully. ${mockStockItems.length} items exported to CSV`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllDataExcel = async () => {
    if (mockStockItems.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      // Show warning for large datasets
      if (mockStockItems.length > 1000) {
        toast.info("Large export in progress...");
      }

      // Simulate processing delay
      await new Promise((resolve) => setTimeout(resolve, mockStockItems.length > 1000 ? 2000 : 500));

      const allColumns = getAllColumns(ALL_COLUMNS);
      const preparedData = prepareDataForExport(mockStockItems, allColumns);
      const filename = `stock-items-all-data-${getCurrentDateForFilename()}.xlsx`;

      exportToExcel(
        preparedData,
        allColumns.map((col) => col.name),
        filename,
      );
      toast.success(`Export completed successfully. ${mockStockItems.length} items exported to Excel`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Stock Items</h1>
            {SHOW_DEMO_MODE_BADGES && (
              <>
                {isBackendLoading ? (
                  <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Loading...
                  </Badge>
                ) : isDemo ? (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                    Demo Mode
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    Live Mode
                  </Badge>
                )}
                <div className="flex items-center gap-2 ml-2">
                  <span className={`text-sm ${isDemo ? "text-muted-foreground" : "font-medium"}`}>Demo</span>
                  <Switch
                    checked={isLive}
                    onCheckedChange={(checked) => handleModeToggle(checked)}
                disabled={isBackendLoading}
              />
              <span className={`text-sm ${isLive ? "font-medium" : "text-muted-foreground"}`}>Live</span>
                </div>
              </>
            )}
            <Badge
              variant="outline"
              className={
                valuationMethod === "FIFO"
                  ? "bg-purple-50 text-purple-700 border-purple-200"
                  : "bg-green-50 text-green-700 border-green-200"
              }
            >
              <BarChart3 className="h-3 w-3 mr-1" />
              Valuation: {getValuationMethodDisplayName()}
            </Badge>
          </div>
          <HelpButton onClick={toggleHelp} variant="outline" size="default" showLabel />
        </div>
        <p className="text-muted-foreground mt-2">
          View and manage all stock items across your warehouses and locations.
        </p>
      </div>

      {/* Action Toolbar with Search */}
      <div className="flex items-center gap-4 w-full">
        <div className="relative w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by code or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Button
          variant={activeFilterChips.length > 0 ? "default" : "outline"}
          onClick={() => setIsFiltersModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Filter className="h-4 w-4" />
          Advanced Filters
          {activeFilterChips.length > 0 && (
            <Badge variant="secondary" className="ml-1 bg-white text-primary">
              {activeFilterChips.length}
            </Badge>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => setIsConfigureColumnsModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Settings2 className="h-4 w-4" />
          Configure Columns
        </Button>

        {/* Export Button with Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              disabled={isExporting}
              aria-label="Export options"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Export
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={handleExportCurrentViewCSV} className="cursor-pointer">
              Export Current View to CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportCurrentViewExcel} className="cursor-pointer">
              Export Current View to Excel
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleExportAllDataCSV} className="cursor-pointer">
              Export All Data to CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportAllDataExcel} className="cursor-pointer">
              Export All Data to Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Add Stock Item Button */}
        <Button className="flex items-center gap-2 ml-auto" onClick={() => navigateTo("create-stock-item")}>
          <Package className="h-4 w-4" />
          Add Stock Item
        </Button>
      </div>

      {/* Active Filter Chips */}
      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilterChips.map((chip, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-300 pl-3 pr-2 py-1.5 text-sm font-normal"
            >
              {chip.displayText}
              <button
                onClick={() => handleRemoveFilterChip(index)}
                className="ml-2 hover:text-teal-900 dark:hover:text-teal-100 transition-colors"
                aria-label="Remove filter"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <button
            onClick={handleClearAllFilters}
            className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedItems.size > 0 && (
        <div
          className="flex items-center justify-between p-3 rounded-lg transition-all duration-200 ease-in-out"
          style={{ backgroundColor: "hsl(var(--access-soft-teal))" }}
        >
          <div className="text-sm font-medium">
            {selectedItems.size} {selectedItems.size === 1 ? "item" : "items"} selected
          </div>
          <div className="flex items-center gap-2">
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button variant="secondary" size="sm" onClick={handleStartStockMovement}>
              <MoveRight className="h-4 w-4 mr-2" />
              Start Stock Movement
            </Button>
            <Button variant="secondary" size="sm" onClick={handleStartStockTake}>
              <ClipboardList className="h-4 w-4 mr-2" />
              Start Stock Take
            </Button>
            {selectedItems.size === 1 && (
              <Button variant="secondary" size="sm" onClick={handleDuplicate}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSelection}
              className="text-muted-foreground hover:text-foreground"
            >
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Table or Empty State */}
      {sortedItems.length > 0 ? (
        <DataTableWrapper
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={totalStockItemsCount}
          itemsPerPage={itemsPerPage}
        >
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={allPageItemsSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all items on this page"
                    className={somePageItemsSelected ? "data-[state=checked]:bg-primary/50" : ""}
                  />
                </TableHead>
                {/* Render columns dynamically based on columnOrder */}
                {getOrderedVisibleColumns().map((columnId) => renderTableHeaderCell(columnId))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedItems.map((item, index) => (
                <TableRow
                  key={item.code}
                  className={`${
                    index % 2 === 0 ? "bg-muted/30" : ""
                  } cursor-pointer hover:bg-muted/50 transition-colors`}
                  onClick={() => handleRowClick(item)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedItems.has(item.code)}
                      onCheckedChange={(checked) => handleSelectItem(item.code, checked as boolean)}
                      aria-label={`Select ${item.code}`}
                    />
                  </TableCell>
                  {/* Render cells dynamically based on columnOrder */}
                  {getOrderedVisibleColumns().map((columnId) => renderTableCell(item, columnId))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DataTableWrapper>
      ) : (
        <Card className="p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <PackageOpen className="h-16 w-16 text-muted-foreground" />
            <div>
              <h3 className="text-lg font-semibold mb-2">No stock items found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchQuery || activeFilterChips.length > 0
                  ? "Try adjusting your search or filters"
                  : "Get started by creating your first stock item"}
              </p>
              <Button onClick={() => navigate("create-stock-item")}>
                <Plus className="h-4 w-4 mr-2" />
                Create Stock Item
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Items per page selector - moved outside table */}
      {sortedItems.length > 0 && (
        <div className="flex items-center gap-2 mt-4">
          <span className="text-sm text-muted-foreground">Items per page:</span>
          <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
            <SelectTrigger className="w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <StockItemDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        items={selectedItemsData}
        onConfirmDelete={confirmDelete}
      />

      {/* Duplicate Item Dialog */}
      <StockItemDuplicateDialog
        open={isDuplicateDialogOpen}
        onOpenChange={setIsDuplicateDialogOpen}
        validationResult={duplicateValidationResult}
        allStockCodes={stockItems.map((item) => item.code)}
        onConfirm={confirmDuplicate}
      />

      {/* Stock Movement Start Dialog */}
      <StockMovementStartDialog
        open={isStockMovementDialogOpen}
        onOpenChange={setIsStockMovementDialogOpen}
        selectedItems={selectedItemsData}
        onConfirm={confirmStockMovement}
      />

      {/* Movement Type Selection Dialog */}
      <MovementTypeSelectionDialog
        open={isMovementTypeDialogOpen}
        onOpenChange={setIsMovementTypeDialogOpen}
        onSelectType={handleMovementTypeSelection}
      />

      {/* Stock Take Start Dialog */}
      <StockTakeStartDialog
        open={isStockTakeDialogOpen}
        onOpenChange={setIsStockTakeDialogOpen}
        validationResult={stockTakeValidationResult}
        onConfirm={confirmStockTake}
      />

      {/* Discard Changes Confirmation Dialog */}
      <AlertDialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDiscard}>Keep Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stock Item Details Drawer */}
      <Sheet open={isDrawerOpen} onOpenChange={handleDrawerClose}>
        <SheetContent side="right" className="w-full sm:w-[800px] sm:max-w-[800px] p-0 flex flex-col">
          {selectedDrawerItem && (
            <>
              {/* Header */}
              <SheetHeader className="bg-muted p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-2xl font-semibold">{selectedDrawerItem.code}</div>
                    <div className="text-sm text-muted-foreground mt-1">{selectedDrawerItem.description}</div>
                  </div>
                  <div className="flex items-center gap-2 pr-5">
                    {selectedDrawerItem.inactive ? (
                      <Badge variant="secondary" className="bg-muted text-muted-foreground">
                        Inactive
                      </Badge>
                    ) : (
                      <Badge variant="success" accent="success">
                        Active
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenInFullView}
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open in Full View
                    </Button>
                    {selectedDrawerItem?.transactionCount !== undefined && selectedDrawerItem.transactionCount > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {selectedDrawerItem.transactionCount} transaction
                        {selectedDrawerItem.transactionCount !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">View</span>
                    <Switch checked={isEditMode} onCheckedChange={setIsEditMode} />
                    <span className="text-sm text-muted-foreground">Edit</span>
                  </div>
                </div>
              </SheetHeader>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-4 bg-background">
                <StockItemDrawerDetails
                  item={selectedDrawerItem}
                  isEditMode={isEditMode}
                  onItemChange={(updatedItem) => setSelectedDrawerItem(updatedItem)}
                  onRegisterCustomFieldsSaveHandler={(fn) => {
                    customFieldsSaveHandlerRef.current = fn;
                  }}
                  isLiveMode={isLiveMode}
                  isFullViewMode={false}
                  onNavigateToTransaction={handleNavigateToTransaction}
                  allStockItems={stockItems}
                />
              </div>

              {/* Footer */}
              <SheetFooter className="bg-muted p-3 border-t flex-row justify-end gap-2">
                <Button variant="outline" onClick={() => handleDrawerClose(false)}>
                  Cancel
                </Button>
                {isEditMode && <Button onClick={handleSaveChanges}>Save Changes</Button>}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        open={isFiltersModalOpen}
        onOpenChange={setIsFiltersModalOpen}
        onApplyFilters={handleApplyFilters}
        initialFilters={activeFilterRows}
        initialLogic={activeFilterLogic}
        customFields={gridFilterFields}
      />

      {/* Configure Columns Modal */}
      <ConfigureColumnsModal
        open={isConfigureColumnsModalOpen}
        onOpenChange={setIsConfigureColumnsModalOpen}
        columns={allColumnsWithCustomFields}
        currentConfig={columnConfig}
        defaultConfig={{ visibleColumns: DEFAULT_VISIBLE_COLUMNS, columnOrder: DEFAULT_VISIBLE_COLUMNS }}
        onSave={(config) => {
          setColumnConfig(config);
        }}
      />

      {/* Contextual Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
        onNavigateToPage={(pageId) => navigateTo(pageId)}
      />
    </div>
  );
}
